import React from "react";
import { DrawerActions, StackActions } from "@react-navigation/native";
import { StackHeaderProps } from "@react-navigation/stack";
import { StyleSheet, TouchableOpacity, useWindowDimensions, View } from "react-native";
import { useSelector } from "react-redux";

import * as connectionDb from "src/db/connection.db";
import { useUserProfile } from "src/hooks/ui";
import { kColors } from "src/kColors";
import { kLoginRouteNames, kRoute, kStorefrontRouteNames } from "src/kRoute";
import { kStyleConst } from "src/kStyle";
import { kWindowBreakpoints } from "src/redux/navigation/thunkActions";

import { PdIconButton } from "../forms/icon-button";
import { Logo, PdArrowLeft, PdMenu, PdShoppingBag } from "../Icons";
import { Cart } from "../impComponents";
import { tSvgProps } from "../types";
import { PdButton, PdButtonLabel } from "../forms/button";

const MenuIcon: React.FC<tSvgProps> = (props) => {
  const rUnreadThreadsQuantity = useSelector(
    (state) =>
      Object.values(state.MessageThreadsReducer.rThreads).filter(
        (thread) => thread.isUnread && thread.recipientStr !== "The Main Tab | Activity",
      ).length,
  );

  return (
    <>
      <PdMenu {...props} />
      {!!rUnreadThreadsQuantity && <View style={styles.pendingIndicator} />}
    </>
  );
};

const CartIcon: React.FC = () => {
  const rCarts = useSelector((state) => state.UserReducer.cart);
  return (
    <>
      <Cart style={styles.headerButtonIcon} />
      {!!rCarts && rCarts.length > 0 && <View style={[styles.pendingIndicator, styles.pendingCart]} />}
    </>
  );
};

const StackHeader: React.FC<StackHeaderProps> = ({ scene, previous, navigation }) => {
  const dimensions = useWindowDimensions();
  const rCurrentRouteName = useSelector((state) => state.NavigationReducer.currentRouteName);
  const rCompany = useSelector((state) => state.CompanyReducer);
  const { isAdmin, isBrand, isRep, isRetailer } = useUserProfile();

  const isLoginRouteName = !!rCurrentRouteName && kLoginRouteNames.includes(rCurrentRouteName);
  const isStorefrontRouteName = !!rCurrentRouteName && kStorefrontRouteNames.includes(rCurrentRouteName);
  const isMobile = dimensions.width < kWindowBreakpoints.md;
  const drawerType = !isStorefrontRouteName && !isMobile ? "permanent" : "front";

  const showMenuButton = drawerType === "front" && !isLoginRouteName;
  const showShopButton = (isAdmin || isRetailer) && !isStorefrontRouteName;
  const showMyStoreButton = (isBrand || isRep) && !isStorefrontRouteName;
  const showCartButton = (isRetailer || (!isRetailer && isStorefrontRouteName)) && !isAdmin;

  const handleToggleDrawer = () => {
    navigation.dispatch(DrawerActions.toggleDrawer());
  };

  const handleOnPressBackButton = () => {
    if (navigation.isFocused() && navigation.canGoBack()) {
      navigation.dispatch({
        ...StackActions.pop(),
        source: scene.route.key,
      });
    }
  };

  const handleOnPressLogo = () => {
    if (rCompany.type === "retailer" || rCompany.type === "tmtAdmin") {
      location.href = "/shop";
    } else if (rCompany.type === "brand") {
      location.href = "/dashboard";
    } else if (rCompany.type === "rep") {
      location.href = "/";
    }
  };

  const handleSwitchToStorefront = async () => {
    if (rCompany.type === "retailer" || rCompany.type === "tmtAdmin" || rCompany.type === "none") {
      navigation.navigate(kRoute.P99);
    } else if (rCompany.type === "brand") {
      navigation.navigate(kRoute.P100, { brand: rCompany.name });
    } else if (rCompany.type === "rep") {
      const connectionsQuery = await connectionDb.queryBrandRepConnection({ repId: rCompany.id }).get();
      if (connectionsQuery.size > 1) {
        navigation.navigate(kRoute.P99);
      } else if (connectionsQuery.docs[0]) {
        navigation.navigate(kRoute.P100, { brand: connectionsQuery.docs[0].data().business.name });
      }
    }
  };

  const handleOnPressCart = () => {
    if (isRetailer) {
      navigation.navigate(kRoute.P102);
    } else {
      navigation.navigate(kRoute.P103, { draftId: "" });
    }
  };

  return (
    <View style={styles.mHeaderContainer}>
      <View style={[styles.innerContainer, styles.alignCenter, styles.justifyStart]}>
        {showMenuButton && (
          <PdIconButton
            size="sm"
            variant="ghost"
            onPress={handleToggleDrawer}
            icon={<MenuIcon style={styles.buttonIcon} />}
          />
        )}
        {previous && (
          <PdIconButton
            size="sm"
            variant="ghost"
            onPress={handleOnPressBackButton}
            icon={<PdArrowLeft style={styles.buttonIcon} />}
            style={styles.ml3}
          />
        )}
      </View>
      <View style={[styles.innerContainer, styles.alignCenter]}>
        <TouchableOpacity style={styles.mHeaderCenter} onPress={handleOnPressLogo}>
          <Logo style={isMobile ? styles.logoMobile : styles.logo} />
        </TouchableOpacity>
      </View>
      <View style={[styles.innerContainer, styles.alignCenter, styles.justifyEnd]}>
        {showShopButton && (
          <>
            {isMobile ? (
              <PdIconButton
                size="sm"
                variant="ghost"
                icon={<PdShoppingBag style={styles.buttonIcon} />}
                onPress={handleSwitchToStorefront}
                style={showCartButton ? styles.mr3 : {}}
              />
            ) : (
              <PdButton variant="outline" onPress={handleSwitchToStorefront} style={showCartButton ? styles.mr3 : {}}>
                <PdButtonLabel>Shop</PdButtonLabel>
              </PdButton>
            )}
          </>
        )}
        {showMyStoreButton && (
          <>
            {isMobile ? (
              <PdIconButton
                size="sm"
                variant="ghost"
                icon={<PdShoppingBag style={styles.buttonIcon} />}
                onPress={handleSwitchToStorefront}
                style={showCartButton ? styles.mr3 : {}}
              />
            ) : (
              <PdButton variant="outline" onPress={handleSwitchToStorefront} style={showCartButton ? styles.mr3 : {}}>
                <PdButtonLabel>{isRep ? "My Brands" : "My Store"}</PdButtonLabel>
              </PdButton>
            )}
          </>
        )}
        {showCartButton && <PdIconButton size="sm" variant="ghost" onPress={handleOnPressCart} icon={<CartIcon />} />}
      </View>
    </View>
  );
};

export default React.memo(StackHeader);

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  buttonIcon: {
    width: 22,
  },
  headerButtonIcon: {
    width: 25,
  },
  innerContainer: {
    flex: 1,
    flexDirection: "row",
    height: "100%",
  },
  justifyEnd: {
    justifyContent: "flex-end",
  },
  justifyStart: {
    justifyContent: "flex-start",
  },
  logo: {
    color: kColors.brandDark,
    width: 250,
  },
  logoMobile: {
    color: kColors.brandDark,
    width: 150,
  },
  mHeaderCenter: {
    alignContent: "center",
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
    justifyItems: "center",
  },
  mHeaderContainer: {
    alignItems: "center",
    backgroundColor: kColors.white,
    borderBottomColor: kColors.brandGreen,
    borderBottomWidth: 1,
    flexDirection: "row",
    height: kStyleConst.kHeaderHeight,
    paddingHorizontal: 5 * kStyleConst.kSpacing,
  },
  ml3: {
    marginLeft: kStyleConst.kSpacing * 3,
  },
  mr3: {
    marginRight: kStyleConst.kSpacing * 3,
  },
  pendingCart: {
    paddingHorizontal: kStyleConst.kSpacing,
  },
  pendingIndicator: {
    backgroundColor: kColors.brandCoral,
    borderRadius: kStyleConst.kCartBadgeDimension / 2,
    height: kStyleConst.kCartBadgeDimension,
    position: "absolute",
    right: -2,
    top: -2,
    width: kStyleConst.kCartBadgeDimension,
  },
});
